import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { format, parse, isValid } from 'date-fns';
import { enUS as enUSFns, es as esFns, fr as frFns, de as deFns, ca as caFns } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { enUS, es, fr, de, ca } from 'react-day-picker/locale';
import { useLocale } from '../contexts/LocaleContext';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';

const LOCALE_MAP: Record<string, Locale> = {
  en: enUS,
  es,
  fr,
  de,
  ca,
};

const DATE_FNS_LOCALE_MAP: Record<string, Locale> = {
  en: enUSFns,
  es: esFns,
  fr: frFns,
  de: deFns,
  ca: caFns,
};

function tryParseDateInput(raw: string, dateFnsLocale: Locale): Date | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const iso = parse(trimmed, 'yyyy-MM-dd', new Date());
  if (isValid(iso)) return iso;
  const pShort = parse(trimmed, 'P', new Date(), { locale: dateFnsLocale });
  if (isValid(pShort)) return pShort;
  const pMedium = parse(trimmed, 'PP', new Date(), { locale: dateFnsLocale });
  if (isValid(pMedium)) return pMedium;
  const pLong = parse(trimmed, 'PPP', new Date(), { locale: dateFnsLocale });
  if (isValid(pLong)) return pLong;
  return undefined;
}

function isAllowedCalendarDate(date: Date, fromDate?: Date, toDate?: Date): boolean {
  if (fromDate && date < fromDate) return false;
  if (toDate && date > toDate) return false;
  return true;
}

export interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  fromDate?: Date;
  toDate?: Date;
  allowEmpty?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  id,
  className,
  fromDate,
  toDate,
  allowEmpty = false,
}) => {
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(() => new Date());
  const [inputValue, setInputValue] = useState('');
  const skipValueSyncRef = useRef(false);

  const dayPickerLocale = useMemo(() => LOCALE_MAP[locale] ?? enUS, [locale]);
  const dateFnsLocale = useMemo(() => DATE_FNS_LOCALE_MAP[locale] ?? enUSFns, [locale]);

  const localeFormatPlaceholder = useMemo(() => {
    const sample = new Date(2026, 2, 20);
    return format(sample, 'P', { locale: dateFnsLocale });
  }, [dateFnsLocale]);

  const resolvedPlaceholder = placeholder === undefined ? localeFormatPlaceholder : placeholder;

  const selectedDate = value?.trim()
    ? (() => {
        const d = parse(value, 'yyyy-MM-dd', new Date());
        return isValid(d) ? d : undefined;
      })()
    : undefined;

  useEffect(() => {
    if (skipValueSyncRef.current) {
      return;
    }
    if (!value?.trim()) {
      setInputValue('');
      setMonth(new Date());
      return;
    }
    const d = parse(value, 'yyyy-MM-dd', new Date());
    if (isValid(d)) {
      setInputValue(format(d, 'P', { locale: dateFnsLocale }));
      setMonth(d);
    }
  }, [value, dateFnsLocale]);

  const commitIfAllowed = useCallback(
    (date: Date): void => {
      if (!isAllowedCalendarDate(date, fromDate, toDate)) return;
      onChange(format(date, 'yyyy-MM-dd'));
      setInputValue(format(date, 'P', { locale: dateFnsLocale }));
      setMonth(date);
    },
    [fromDate, toDate, onChange, dateFnsLocale]
  );

  const handleSelect = (date: Date | undefined): void => {
    if (!date) return;
    if (!isAllowedCalendarDate(date, fromDate, toDate)) return;
    commitIfAllowed(date);
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const v = e.target.value;
    setInputValue(v);
    if (allowEmpty && !v.trim()) {
      onChange('');
    }
  };

  const handleInputBlur = (): void => {
    try {
      if (allowEmpty && !inputValue.trim()) {
        onChange('');
        setInputValue('');
        return;
      }
      const parsed = tryParseDateInput(inputValue, dateFnsLocale);
      if (parsed && isValid(parsed) && isAllowedCalendarDate(parsed, fromDate, toDate)) {
        commitIfAllowed(parsed);
        return;
      }
      if (value?.trim()) {
        const d = parse(value, 'yyyy-MM-dd', new Date());
        if (isValid(d)) setInputValue(format(d, 'P', { locale: dateFnsLocale }));
      } else {
        setInputValue('');
      }
    } finally {
      skipValueSyncRef.current = false;
    }
  };

  return (
    <div
      className={cn(
        'flex h-[38px] w-full rounded-[10px] border border-input bg-card shadow-sm transition-[border-color,box-shadow] duration-200 overflow-hidden focus-within:border-[var(--scribane-green)] focus-within:ring-[3px] focus-within:ring-[rgba(94,204,128,0.4)]',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <Input
        id={id}
        type="text"
        value={inputValue}
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        onChange={handleInputChange}
        onFocus={() => {
          skipValueSyncRef.current = true;
        }}
        onBlur={handleInputBlur}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="h-[38px] flex-1 min-w-0 rounded-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-3"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            className="h-[38px] w-[38px] shrink-0 rounded-none border-0 border-l border-input shadow-none"
            aria-label="Select date"
          >
            <CalendarIcon className="size-4" />
            <span className="sr-only">Select date</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="z-[2010] w-auto overflow-hidden p-0"
          align="end"
          alignOffset={-8}
          sideOffset={10}
        >
          <Calendar
            mode="single"
            locale={dayPickerLocale}
            month={month}
            onMonthChange={setMonth}
            selected={selectedDate}
            onSelect={handleSelect}
            disabled={(date) => {
              if (fromDate && date < fromDate) return true;
              if (toDate && date > toDate) return true;
              return false;
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
